// The deployment dashboard: every site the daily run works, one dense row each, in the
// operator's planned deployment order (or A-Z, by committed COD, or by achievable kWp). The app's landing page.
// See docs/Y2026-Q3/design-deployment-dashboard.md.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useQueryClient } from "react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  type DashboardRow,
  type RagVerdict,
  useDashboard,
  useMoveDashboardRow,
} from "../api/hooks/useDashboard";
import { DashboardTotals } from "../components/DashboardTotals";
import { DeploymentTable } from "../components/DeploymentTable";
import { ApplyingReviewNotice } from "../components/ApplyingReview";
import { ReviewProvider } from "../components/ReviewProvider";
import { MultiSelectFilter } from "../components/MultiSelectFilter";
import { PageHeader } from "../components/PageHeader";
import { useOtherSlotsInUse } from "../api/hooks/useAgentConfigs";
import { DEFAULT_ONLY_NOTE } from "../utils/agentSlotText";
import { achievableKwp } from "../utils/achievableKwp";
import { bmaStatusLabel, inPipelineOrder } from "../utils/bmaStatus";
import { projectMatchesSearch } from "../utils/projectOrgs";
import { oldestBlockerDays } from "../utils/nextActions";
import { smallestFigure } from "../utils/attributeFormat";
import { reviewAttrs } from "../utils/reviewState";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import styles from "./DashboardRoute.module.css";

type SortMode = "planned" | "name" | "cod" | "kwp" | "blocked";
const SORT_MODES: SortMode[] = ["planned", "name", "cod", "kwp", "blocked"];
const SORT_KEY = "inrange-agents.dashboard.sort";

interface Filters {
  redOnly: boolean;
  // Any of these; none selected shows every site.
  tenants: string[];
  landlords: string[];
  // Statuses on admin.inrange.io, NO_STATUS for a site that has none recorded.
  statuses: string[];
  // Free text over the site's name and its linked site's name on admin.inrange.io.
  search: string;
}
const NO_FILTERS: Filters = {
  redOnly: false,
  tenants: [],
  landlords: [],
  statuses: [],
  search: "",
};
// bma_status is never empty, so this cannot collide with a real status.
const NO_STATUS = "";
const statusOf = (row: DashboardRow): string => row.bma_status || NO_STATUS;
const statusLabel = (status: string): string =>
  status === NO_STATUS ? "No platform status" : bmaStatusLabel(status);
// Kept for the browser session, so they survive navigating away and back.
const FILTERS_KEY = "inrange-agents.dashboard.filters";

// A per-viewer convenience: unreadable storage just means the planned order.
const storedSort = (): SortMode => {
  try {
    const stored = window.localStorage.getItem(SORT_KEY);
    return SORT_MODES.find((m) => m === stored) ?? "planned";
  } catch {
    return "planned";
  }
};

const storedFilters = (): Filters => {
  try {
    const stored: unknown = JSON.parse(
      window.sessionStorage.getItem(FILTERS_KEY) ?? "null",
    );
    if (!stored || typeof stored !== "object") return NO_FILTERS;
    const s = stored as Partial<
      Record<keyof Filters | "tenant" | "landlord", unknown>
    >;
    // A session saved before the multi-select held one name under "tenant" / "landlord".
    const names = (list: unknown, single: unknown): string[] =>
      Array.isArray(list)
        ? list.filter((n): n is string => typeof n === "string")
        : typeof single === "string" && single
          ? [single]
          : [];
    return {
      redOnly: s.redOnly === true,
      tenants: names(s.tenants, s.tenant),
      landlords: names(s.landlords, s.landlord),
      statuses: names(s.statuses, undefined),
      search: typeof s.search === "string" ? s.search : "",
    };
  } catch {
    return NO_FILTERS;
  }
};

// Sites by a figure, reading the smallest option where options are still
// open (the earliest COD, as the schedule test does; the smaller kWp, as the totals do,
// inferred from grid kWac where that is the only limit on a route).
// Sites without a figure follow, keeping the planned order among themselves and among
// equal figures.
const byFigure = (
  projects: DashboardRow[],
  read: (row: DashboardRow) => number | string | undefined,
  order: "ascending" | "descending",
): DashboardRow[] =>
  [...projects].sort((a, b) => {
    const fa = read(a);
    const fb = read(b);
    if (fa === undefined || fb === undefined)
      return fa === fb ? 0 : fa === undefined ? 1 : -1;
    const ascending = String(fa).localeCompare(String(fb), undefined, {
      numeric: true,
    });
    return order === "ascending" ? ascending : -ascending;
  });

const docOf = (row: DashboardRow): string => `project:${row.project_id}`;

// The verdict the RAG chip leads with: the Manager's, else the computed tests'.
const headlineVerdict = (row: DashboardRow): RagVerdict =>
  row.summary?.summary.rag.verdict ?? row.rag.verdict;

// `name` prefers the operator's friendly name, so the platform's name is searched too.
const searchName = (row: DashboardRow): string =>
  [row.name, row.bma_site_name].filter(Boolean).join(" ");

// An owner-occupier is both the tenant and the landlord.
const ROLE_PARTIES = {
  tenant: ["Tenant", "Owner-occupier"],
  landlord: ["Landlord", "Owner-occupier"],
} as const;

const partyNames = (
  row: DashboardRow,
  side: keyof typeof ROLE_PARTIES,
): string[] =>
  row.parties
    .filter((p) => (ROLE_PARTIES[side] as readonly string[]).includes(p.role))
    .map((p) => p.name);

// A site matches when any chosen party is its tenant (or landlord); none chosen, every site.
const matches = (
  row: DashboardRow,
  side: keyof typeof ROLE_PARTIES,
  chosen: string[],
) =>
  chosen.length === 0 || partyNames(row, side).some((n) => chosen.includes(n));

const choices = (
  rows: DashboardRow[],
  side: keyof typeof ROLE_PARTIES,
): string[] =>
  [...new Set(rows.flatMap((r) => partyNames(r, side)))].sort((a, b) =>
    a.localeCompare(b, "en-GB"),
  );

export const DashboardRoute = () => {
  useDocumentTitle("Dashboard");
  const otherSlots = useOtherSlotsInUse();
  const { user } = useKindeAuth();
  const { data, isLoading, error } = useDashboard();
  const move = useMoveDashboardRow();
  const [sort, setSort] = useState<SortMode>(storedSort);
  // When review comments on a row have been applied, everything on the page reloads, so
  // the row shows the rebuilt briefing.
  const qc = useQueryClient();
  const wasApplying = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = new Set(
      (data?.projects ?? [])
        .filter((p) => p.applying_review)
        .map((p) => p.project_id),
    );
    const finished = [...wasApplying.current].filter((id) => !now.has(id));
    wasApplying.current = now;
    if (finished.length === 0) return;
    void qc.invalidateQueries();
  }, [data, qc]);
  const [stored, setStored] = useState<Filters>(storedFilters);

  const chooseSort = (mode: SortMode) => {
    setSort(mode);
    try {
      window.localStorage.setItem(SORT_KEY, mode);
    } catch {
      // Not remembered; the choice still holds for this visit.
    }
  };

  const chooseFilters = (next: Filters) => {
    setStored(next);
    try {
      window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(next));
    } catch {
      // Not remembered; the filters still hold for this visit.
    }
  };

  const projects = useMemo(() => data?.projects ?? [], [data]);
  const tenants = useMemo(() => choices(projects, "tenant"), [projects]);
  const landlords = useMemo(() => choices(projects, "landlord"), [projects]);
  const statuses = useMemo(() => {
    const present = projects.map(statusOf);
    return [
      ...inPipelineOrder(present.filter((s) => s !== NO_STATUS)),
      ...(present.includes(NO_STATUS) ? [NO_STATUS] : []),
    ];
  }, [projects]);
  // A remembered choice no longer on any site would hide rows behind a choice the
  // dropdown cannot show, so it is dropped.
  const filters: Filters = useMemo(
    () => ({
      redOnly: stored.redOnly,
      tenants: stored.tenants.filter((n) => tenants.includes(n)),
      landlords: stored.landlords.filter((n) => landlords.includes(n)),
      statuses: stored.statuses.filter((s) => statuses.includes(s)),
      search: stored.search,
    }),
    [stored, tenants, landlords, statuses],
  );
  // Clear filters leaves the search, which its box clears itself.
  const chosen =
    filters.redOnly ||
    filters.tenants.length > 0 ||
    filters.landlords.length > 0 ||
    filters.statuses.length > 0;
  const filtered = chosen || filters.search.trim() !== "";

  const rows = useMemo(() => {
    const shown = projects.filter(
      (r) =>
        (!filters.redOnly || headlineVerdict(r) === "red") &&
        matches(r, "tenant", filters.tenants) &&
        matches(r, "landlord", filters.landlords) &&
        (filters.statuses.length === 0 ||
          filters.statuses.includes(statusOf(r))) &&
        projectMatchesSearch(searchName(r), null, filters.search),
    );
    if (sort === "blocked")
      return [...shown].sort(
        (a, b) => oldestBlockerDays(b) - oldestBlockerDays(a),
      );
    if (sort === "cod")
      return byFigure(
        shown,
        (r) => smallestFigure(r.attributes["committed_cod"]),
        "ascending",
      );
    if (sort === "kwp")
      return byFigure(shown, (r) => achievableKwp(r)?.kwp, "descending");
    return sort === "name"
      ? [...shown].sort((a, b) =>
          (a.name || a.project_id).localeCompare(
            b.name || b.project_id,
            "en-GB",
          ),
        )
      : shown;
  }, [projects, sort, filters]);

  return (
    <>
      <PageHeader title="Dashboard" description={otherSlots && DEFAULT_ONLY_NOTE} />
      <section className={styles.page}>
        {projects.length > 0 && <DashboardTotals rows={rows} />}
        {projects.length > 0 && (
          <div className={styles.filters} role="group" aria-label="Show">
            <span className={styles.show}>Show</span>
            <button
              type="button"
              className={`${styles.chip} ${filters.redOnly ? styles.chipOn : ""}`}
              aria-pressed={filters.redOnly}
              onClick={() =>
                chooseFilters({ ...filters, redOnly: !filters.redOnly })
              }
            >
              <span className={styles.dot} aria-hidden />
              Red only
            </button>
            <MultiSelectFilter
              noun={{ one: "tenant", many: "tenants" }}
              size="compact"
              options={tenants}
              selected={filters.tenants}
              onChange={(next) => chooseFilters({ ...filters, tenants: next })}
            />
            <MultiSelectFilter
              noun={{ one: "landlord", many: "landlords" }}
              size="compact"
              options={landlords}
              selected={filters.landlords}
              onChange={(next) => chooseFilters({ ...filters, landlords: next })}
            />
            <MultiSelectFilter
              noun={{ one: "status", many: "statuses" }}
              size="compact"
              searchable={false}
              options={statuses}
              selected={filters.statuses}
              labelOf={statusLabel}
              onChange={(next) => chooseFilters({ ...filters, statuses: next })}
            />
            {chosen && (
              <button
                type="button"
                className={styles.clear}
                onClick={() =>
                  chooseFilters({ ...NO_FILTERS, search: filters.search })
                }
              >
                Clear filters
              </button>
            )}
            <span className={styles.count}>
              Showing {rows.length} of {projects.length} site
              {projects.length === 1 ? "" : "s"}
            </span>
            <span className={styles.divider} aria-hidden />
            <label className={styles.control}>
              Sort
              <select
                value={sort}
                onChange={(e) => chooseSort(e.target.value as SortMode)}
              >
                <option value="planned">Planned deployment order</option>
                <option value="name">Name, A-Z</option>
                <option value="cod">Committed COD, earliest first</option>
                <option value="kwp">By achievable kWp, largest first</option>
                <option value="blocked">Oldest blocker first</option>
              </select>
            </label>
            <input
              type="search"
              className={styles.search}
              placeholder="Search sites…"
              aria-label="Search sites by name or admin.inrange.io name"
              value={filters.search}
              onChange={(e) =>
                chooseFilters({ ...filters, search: e.target.value })
              }
            />
          </div>
        )}
        {move.error && (
          <p className={styles.error}>
            Could not move that site: {move.error.message}
          </p>
        )}
        {isLoading ? (
          <p className={styles.muted}>Loading the dashboard…</p>
        ) : error ? (
          <p className={styles.error}>Could not load the dashboard.</p>
        ) : projects.length === 0 ? (
          <p className={styles.muted}>
            No project is on the daily run yet. Turn agents on for a
            project in its Settings tab and it appears here.
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.muted}>No site matches these filters.</p>
        ) : (
          <ReviewProvider
            available
            defaultDoc={null}
            defaultLevel="dashboard_row"
            comments={[]}
            projectId={null}
            onQuote={null}
          >
            <DeploymentTable
              attributes={data?.attributes ?? []}
              rows={rows}
              sitePage={false}
              rowAttrs={(row) =>
                reviewAttrs(
                  "dashboard_row",
                  `${row.name || row.project_id} dashboard row`,
                  {
                    doc: docOf(row),
                  },
                )
              }
              rowAction={(row) => (
                <>
                  {row.applying_review && (
                    <ApplyingReviewNotice
                      applying={row.applying_review}
                      projectId={row.project_id}
                    />
                  )}
                </>
              )}
              moving={move.isLoading}
              // A move's position counts every site, so moving needs the whole planned order.
              onMove={
                sort === "planned" && !filtered
                  ? (projectId, to) =>
                      move.mutate({
                        project_id: projectId,
                        to,
                        actor_email: user?.email ?? "",
                      })
                  : undefined
              }
            />
          </ReviewProvider>
        )}
      </section>
    </>
  );
};
